import { Loader, Stack, Text } from '@mantine/core';
/**
 * OutputMarkdown — streamed output + status-specific empty states.
 *
 * States (in priority order):
 *   1. text non-empty            → rendered markdown
 *   2. status === 'streaming'    → loader + elapsed-time counter
 *   3. text empty + idle/done    → "Fill in the brief…" prompt
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';

type RunStatus = 'idle' | 'streaming' | 'done' | 'error';

interface OutputMarkdownProps {
  text: string;
  status?: RunStatus;
}

/** mm:ss formatter. Always at least 1 digit minute (0:09, 0:59, 1:00, 12:34). */
function fmtElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Sub-component so the timer's 1Hz tick doesn't re-render parent on output updates. */
function GeneratingScreen() {
  const startedAt = useRef<number>(Date.now());
  const [now, setNow] = useState<number>(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const elapsed = fmtElapsed(now - startedAt.current);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '24px',
        background:
          'linear-gradient(180deg, rgba(18, 107, 87, 0.04) 0%, rgba(18, 107, 87, 0.10) 100%)',
      }}
      data-testid="output-markdown-loading"
    >
      <Stack align="center" gap="md" maw={420}>
        <Loader size="xl" color="accent" type="dots" />
        <Text fz="lg" fw={600} ta="center" c="var(--accent, #126b57)">
          Generating ad…
        </Text>
        <Text
          fz="xl"
          fw={500}
          ta="center"
          c="var(--accent, #126b57)"
          ff="monospace"
          data-testid="generation-elapsed"
          style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em' }}
        >
          {elapsed}
        </Text>
        <Text c="dimmed" fz="sm" ta="center">
          Drafting your ad. Output will appear here as it is produced.
        </Text>
        <Text c="dimmed" fz="xs" ta="center">
          You can leave this page — the run continues in the background and will appear in the Runs
          sidebar when it is done.
        </Text>
      </Stack>
    </div>
  );
}

export function OutputMarkdown({ text, status = 'idle' }: OutputMarkdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [userScrolledUp, setUserScrolledUp] = useState(false);

  useLayoutEffect(() => {
    if (!text || status === 'streaming') return;

    const el = containerRef.current;
    if (!el) return;

    el.scrollTop = 0;
    setUserScrolledUp(false);
  }, [text, status]);

  useLayoutEffect(() => {
    if (!text || status !== 'streaming') return;

    const el = containerRef.current;
    if (!el) return;

    if (userScrolledUp) return;
    el.scrollTop = el.scrollHeight;
  }, [text, status, userScrolledUp]);

  function handleScroll() {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setUserScrolledUp(distanceFromBottom > 50);
  }

  if (!text) {
    if (status === 'streaming') {
      return <GeneratingScreen />;
    }
    return (
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '12px',
        }}
        data-testid="output-markdown-empty"
      >
        <Text c="dimmed" fz="sm" ta="center">
          Fill in the brief on the left, then click <strong>Generate Ad</strong>.
        </Text>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        overflowY: 'auto',
        height: '100%',
        padding: '12px',
      }}
      data-testid="output-markdown"
    >
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
}
